<template>
  <div class="max-w-6xl h-full m-auto py-2 px-4 flex flex-col">
    <h2 class="py-4 text-2xl">Tasks Report</h2>

    <div class="py-2 flex space-x-4">
      <div class="flex flex-col">
        <label for="assigned-to">Assigned To</label>
        <select
          v-model="assignedTo"
          @change="setRowData()"
          id="assigned-to"
          class="py-2 px-2 border border-gray-300 rounded"
        >
          <option value=""></option>
          <option
            v-for="user in users"
            :key="user.username"
            :value="user.username"
          >
            {{ user.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col">
        <label for="entered-by">Entered By</label>
        <select
          v-model="enteredBy"
          @change="setRowData()"
          id="entered-by"
          class="py-2 px-2 border border-gray-300 rounded"
        >
          <option value=""></option>
          <option
            v-for="user in users"
            :key="user.username"
            :value="user.username"
          >
            {{ user.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col">
        <div>Scheduled Date</div>
        <v-date-picker
          v-model="scheduledDateRange"
          mode="range"
          @input="setRowData()"
          color="green"
        />
      </div>

      <div class="flex flex-col">
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small sm:mt-6"
          :disabled="!gridHasData"
        >
          Download CSV
          <icon set="outline" name="document-download" class="ml-2 h-6 w-6" />
        </Button>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import TasksReportOrderLink from "./components/TasksReportOrderLink";

const NotesApi = ApiFactory.get("notes");

export default {
  name: "TasksReport",
  components: {
    AgGridVue,
    TasksReportOrderLink, // eslint-disable-line
    Button,
  },
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      users: [],
      assignedTo: null,
      enteredBy: null,
      scheduledDateRange: {
        start: new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000),
        end: new Date(),
      },
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
  },
  mixins: [dateFormatter],
  async created() {
    const response = await ApiFactory.get("feature-flags").isFeatureFlagEnabled("phase3d_care_app");

    if (response.status == 200) {
      if (response.data.body.enabled) {
        await this.$router.push({ name: "Dashboard" });
      }
    }
  },
  beforeMount() {
    this.gridOptions = {};

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "TasksReportOrderLink",
        width: 100,
      },
      {
        headerName: "Date",
        field: "EntryDate",
        width: 200,
      },
      {
        headerName: "By",
        field: "EnteredBy",
        width: 150,
      },
      {
        headerName: "Note",
        field: "Notes",
        width: 400,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Event",
        field: "Event",
        width: 150,
      },
      {
        headerName: "Priority",
        field: "Priority",
        width: 50,
      },
      {
        headerName: "Follow Up",
        field: "FollowupAction",
        width: 150,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Assigned To",
        field: "AssignedTo",
        width: 150,
      },
      {
        headerName: "Completed",
        field: "Completed",
        width: 50,
      },
      {
        headerName: "Actual Date",
        field: "ActualDate",
        width: 200,
      },
    ];

    this.setRowData();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.setUsers();
  },
  methods: {
    setUsers() {
      this.users = [
        { username: "efelker", name: "Evan Felker" },
        { username: "jbouchard", name: "Jennifer Bouchard" },
        { username: "dcary", name: "Danyell Cary" },
        { username: "Twoodward", name: "Tammy Woodward" },
        { username: "jkahle", name: "Jamie Kahle" },
        { username: "rshields", name: "Racheal Shields" },
      ];
    },
    setRowData() {
      if (this.gridOptions.api) {
        this.gridOptions.api.showLoadingOverlay();
      }

      NotesApi.getTasks({
        assigned_to: this.assignedTo,
        entered_by: this.enteredBy,
        scheduled_date_start: this.scheduledDateRange.start
          .toISOString()
          .slice(0, 10),
        scheduled_date_end: this.scheduledDateRange.end
          .toISOString()
          .slice(0, 10),
      }).then((response) => {
        this.rowData = response.data.body.map((row) => {
          return {
            Type: row.Type,
            NumericKey: row.NumericKey,
            SourceOrderNumber: row.SourceOrderNumber,
            EntryDate: row.EntryDate,
            EnteredBy: row.EnteredBy,
            Notes: row.Notes,
            Event: row.Event,
            Priority: row.Priority,
            FollowupAction: row.FollowupAction,
            AssignedTo: row.AssignedTo,
            Completed: row.Completed,
            ActualDate: row.ActualDate,
          };
        });
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
    exportCSV() {
      if (!this.gridHasData) return;
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
      });
    },
  },
  filters: {},
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
